import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import api from "../lib/api";
import { PillBadge } from "./Primitives";
import { useTheme } from "../contexts/ThemeContext";

// Recharts cursor tint — colour looks fine in both themes because it's a red
// accent with low alpha.
const CURSOR_TINT = { fill: "#FF3B3015" };

/** Overview widget: reason-based absence analytics for the last 30 days. */
export default function AbsenceInsights() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const { theme } = useTheme() || { theme: "dark" };
  const tooltipStyle = useMemo(() => ({
    background: theme === "light" ? "#ffffff" : "#0A0A0A",
    border: `1px solid ${theme === "light" ? "#ececee" : "#27272A"}`,
    color: theme === "light" ? "#18181b" : "#ffffff",
    fontSize: 12,
    boxShadow: theme === "light" ? "0 4px 12px rgba(24,24,27,0.08)" : "none",
  }), [theme]);
  const gridStroke = theme === "light" ? "#ececee" : "#27272A";

  useEffect(() => {
    let cancelled = false;
    api.get("/availability/insights?days=30")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setErr(true);
        if (process.env.NODE_ENV !== "production") console.warn("insights failed:", e?.response?.status);
      });
    return () => { cancelled = true; };
  }, []);

  // Non-operators get 403 from the endpoint → we simply don't render the widget.
  if (err) return null;

  const reasons = (data?.by_reason || []).filter((r) => r.count > 0);
  const trend = data?.trend || [];
  const upcoming = data?.upcoming_unavailable_days || 0;

  return (
    <div className="border border-[#27272A] bg-[#121212] flex flex-col" data-testid="absence-insights-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between">
        <div>
          <div className="label-mute">Absences · Last 30 Days</div>
          <div className="font-display uppercase font-bold text-xl mt-1">By Reason</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="label-mute">Upcoming</div>
            <div className="font-mono text-sm text-[#FF9F0A]" data-testid="absence-insights-upcoming">
              {upcoming} day{upcoming === 1 ? "" : "s"}
            </div>
          </div>
          <Link
            to="/availability"
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] border border-[#27272A] hover:border-[#FF3B30] px-2 py-1"
            data-testid="absence-insights-open-availability"
          >Open</Link>
        </div>
      </div>
      <div className="p-5 h-80">
        {reasons.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-zinc-500 text-sm scanlines">
            <div>No unavailable days logged in this window.</div>
            <div className="text-xs mt-1">Everyone&apos;s clear.</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reasons} layout="vertical" margin={{ left: 10, right: 20, top: 8, bottom: 8 }}>
              <CartesianGrid stroke={gridStroke} horizontal={false} />
              <XAxis type="number" stroke="#71717A" fontSize={11} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="reason"
                stroke="#71717A"
                fontSize={11}
                width={110}
              />
              <Tooltip contentStyle={tooltipStyle} cursor={CURSOR_TINT} />
              <Bar dataKey="count" fill="#FF3B30" radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      {trend.length > 0 && (
        <div className="border-t border-[#27272A] px-5 py-3 flex items-center gap-3">
          <div className="label-mute">4-week trend</div>
          <div className="flex-1 flex items-end gap-1 h-8" data-testid="absence-insights-trend">
            {trend.map((t) => {
              const max = Math.max(1, ...trend.map((x) => x.unavailable));
              const pct = Math.round((t.unavailable / max) * 100);
              return (
                <div key={t.label} className="flex-1 flex flex-col items-center justify-end" title={`${t.label}: ${t.unavailable} unavailable`}>
                  <div
                    className="w-full bg-[#FF3B30] transition-all"
                    style={{ height: `${Math.max(4, pct)}%` }}
                  />
                  <div className="text-[9px] text-zinc-600 mt-1 font-mono">{t.label}</div>
                </div>
              );
            })}
          </div>
          <PillBadge tone="red">
            <span data-testid="absence-insights-total">{trend.reduce((a, t) => a + t.unavailable, 0)} total</span>
          </PillBadge>
        </div>
      )}
    </div>
  );
}
